import { useTranslation } from 'react-i18next'
import { Skeleton, Typography } from 'antd'
import { isAppIconSrc } from '@/lib/appIcon'
import { DEFAULT_APP_ICON } from '@/lib/appIcon'
import type { WorkbenchAppCard, WorkbenchCardStatus } from './types'

const statusKey = {
  in_review: 'workbench.statusInReview',
  pending: 'workbench.statusPending',
  published: 'workbench.statusPublished',
  developing: 'workbench.statusDeveloping',
  rejected: 'workbench.statusRejected',
  delisted: 'workbench.statusDelisted',
  disabled: 'workbench.statusDisabled',
  archived: 'workbench.statusArchived',
} as const satisfies Record<WorkbenchCardStatus, string>

interface AppCardProps {
  app: WorkbenchAppCard
  onClick: () => void
}

function AppCard({ app, onClick }: AppCardProps) {
  const { t } = useTranslation()

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full cursor-pointer flex-col rounded-lg border border-[#DCDCDC] bg-white/85 p-2.5 text-left shadow-[2px_2px_30px_0_rgba(0,0,0,0.05)] transition hover:border-[#6366F1]/40"
    >
      {/* Figma: 内层 padding 10；区块 gap 10（非 20） */}
      <div className="flex w-full flex-col gap-2.5 p-2.5">
        <div className="flex gap-5">
          <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center overflow-hidden rounded-[14px] border border-[#DCDCDC] bg-white text-2xl">
            {isAppIconSrc(app.icon) ? (
              <img
                src={app.icon}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = DEFAULT_APP_ICON
                }}
              />
            ) : (
              app.icon
            )}
          </div>
          <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <Typography.Text
                className="!text-lg !leading-[25px] !font-medium !text-black"
                ellipsis
              >
                {app.name}
              </Typography.Text>
              {app.isCollaborator && (
                <span className="rounded-full bg-[rgba(255,141,40,0.08)] px-1.5 py-px text-xs leading-[18px] text-[#FF8D28]">
                  {t('workbench.collaboration')}
                </span>
              )}
            </div>
            <Typography.Text className="!text-[13px] !leading-5 !text-black/60">
              {t('workbench.version')}
              {app.versionLabel}
            </Typography.Text>
          </div>
        </div>

        <div className="py-0.5">
          <div className="h-px w-full bg-black/[0.04]" />
        </div>

        <div className="flex w-full flex-col gap-1">
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-1">
              <Typography.Text className="!text-[13px] !leading-5 !text-black/60">
                {t('workbench.owner')}
                <span className="text-black">
                  {app.isOwner ? t('workbench.me') : app.ownerName}
                </span>
              </Typography.Text>
              <Typography.Text className="!text-[13px] !leading-5 !text-black/60">
                (@{app.owner_username})
              </Typography.Text>
            </div>
            <Typography.Text className="!shrink-0 !text-[13px] !leading-5 !text-black">
              {app.distribution === 'public'
                ? t('workbench.distributionPublic')
                : t('workbench.distributionSpace')}
            </Typography.Text>
          </div>
          <div className="flex items-center gap-2">
            <Typography.Text className="!text-[13px] !leading-5 !text-black/60">
              {t('workbench.updatedAt', { time: app.updated_at })}
            </Typography.Text>
            <span className="flex h-6 shrink-0 items-center rounded-full bg-[rgba(99,102,241,0.05)] px-1.5 text-xs leading-[18px] text-[#6155F5]">
              {t(statusKey[app.status])}
            </span>
          </div>
        </div>
      </div>
    </button>
  )
}

const APP_SKELETON_COUNT = 2

export function AppListSkeleton() {
  const { t } = useTranslation()

  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('common.loading')}
      className="grid grid-cols-1 gap-2.5 md:grid-cols-2"
    >
      {Array.from({ length: APP_SKELETON_COUNT }, (_, index) => (
        <div
          key={index}
          aria-hidden
          className="rounded-lg border border-[#DCDCDC] bg-white/85 p-2.5 shadow-[2px_2px_30px_0_rgba(0,0,0,0.05)]"
        >
          <div className="flex flex-col gap-2.5 p-2.5">
            <div className="flex gap-5">
              <Skeleton.Avatar
                active
                shape="square"
                size={54}
                style={{ borderRadius: 14 }}
              />
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
                <Skeleton.Input
                  active
                  style={{ width: 148, minWidth: 148, height: 25 }}
                />
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 96, minWidth: 96, height: 20 }}
                />
              </div>
            </div>
            <div className="py-0.5">
              <div className="h-px w-full bg-black/[0.04]" />
            </div>
            <Skeleton.Input active block size="small" style={{ height: 20 }} />
            <Skeleton.Input
              active
              size="small"
              style={{ width: 180, minWidth: 180, height: 20 }}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default AppCard
